/**
 * MCP API Service
 *
 * Service for fetching MCP (Model Context Protocol) servers and tools from Azure APIs.
 * Based on LogicAppsUX patterns for discovering MCP-enabled connectors.
 */

import type { DiscoveryOperation } from './operationsService';
import {
  isLocalMode,
  localFetch,
  getLocalRuntimeUrl,
} from './localRuntimeClient';
import { getAppUrl } from './appClient';
import { apiVersions, getLogicEndpoint } from './endpoints';
import { toShortManagedApiId } from '@/utils/connections';

// Local runtime (StandardRuntimeHost / func.exe) accepts the webhook-management API version
// — matches the convention in `agentUrlService.ts` (listApiKeys / listDraftApiKeys).
const LOCAL_RUNTIME_API_VERSION = '2018-11-01';

/**
 * MCP Server from the Azure API
 * Extends DiscoveryOperation with MCP-specific properties
 */
export interface McpServerOperation extends DiscoveryOperation {
  properties: DiscoveryOperation['properties'] & {
    /** MCP server-specific metadata */
    mcpServerInfo?: {
      displayName?: string;
      description?: string;
    };
  };
}

/**
 * MCP Tool operation from a server
 */
export interface McpToolOperation extends DiscoveryOperation {
  properties: DiscoveryOperation['properties'] & {
    /** Tool input schema */
    inputSchema?: Record<string, unknown>;
    /** Tool output schema */
    outputSchema?: Record<string, unknown>;
  };
}

/**
 * Options for fetching MCP servers
 */
export interface McpFetchOptions {
  projectName: string;
  accessToken: string;
}

/**
 * Fetch wrapper that supports both local and cloud modes
 */
async function fetchWithAuth<T>(url: string, accessToken: string): Promise<T> {
  if (isLocalMode()) {
    return localFetch<T>(url);
  }

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`MCP API request failed: ${response.status} ${errorText}`);
  }

  return response.json();
}

/**
 * Azure paginated response shape
 */
interface AzurePaginatedResponse<T> {
  value: T[];
  nextLink?: string;
}

/**
 * Fetch with pagination support for Azure APIs
 */
async function fetchWithPagination<T>(
  url: string,
  accessToken: string
): Promise<T[]> {
  const results: T[] = [];
  let nextLink: string | undefined = url;

  while (nextLink) {
    const response: AzurePaginatedResponse<T> = await fetchWithAuth<AzurePaginatedResponse<T>>(
      nextLink,
      accessToken
    );
    results.push(...(response.value ?? []));
    nextLink = response.nextLink;
  }

  return results;
}

/**
 * Fetches all available MCP servers (connectors with MCP support)
 *
 * Routes through the Otto control plane (`/automationProjects/{project}/apiOperations`)
 * with the `mcpOnly` filter to return connectors that expose MCP server capabilities.
 *
 * @param opts - Fetch options with project name and Otto access token
 * @returns Array of MCP server operations
 */
export async function getMcpServers(
  opts: McpFetchOptions
): Promise<McpServerOperation[]> {
  const url = `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(opts.projectName)}/apiOperations?api-version=${apiVersions.otto}&$filter=mcpOnly eq true`;

  try {
    const operations = await fetchWithPagination<McpServerOperation>(url, opts.accessToken);
    // The endpoint returns one row per MCP server operation. Each operation is a
    // distinct MCP server, so we do NOT dedup by connector — collapsing would
    // destroy the per-operation `name` (the operationId we later need to resolve
    // the MCP server path from swagger). Display names are cleaned up downstream
    // in `mcpServerToInfo` (which prefers `api.displayName`/`summary`), so the
    // project-prefixed `name` is preserved here purely as the operationId.
    return operations;
  } catch (error) {
    console.warn('Failed to fetch MCP servers:', error);
    return [];
  }
}

/**
 * Fetches tools (operations) available from a specific MCP server/connector
 *
 * Routes through the Otto control plane (`/automationProjects/{project}/managedApis/{connector}/apiOperations`)
 * rather than ARM directly, mirroring `fetchOperationsForAzureConnector` in operationsService.
 *
 * @param opts - Fetch options with project name and Otto access token
 * @param connectorId - Connector ID (full ARM path or short `/managedApis/{name}` form)
 * @returns Array of tool operations from the MCP server
 */
export async function getMcpToolsByConnector(
  opts: { projectName: string; accessToken: string },
  connectorId: string
): Promise<McpToolOperation[]> {
  const shortId = toShortManagedApiId(connectorId);
  const match = shortId.match(/\/managedApis\/([^/]+)$/i);
  const connectorName = match?.[1];
  if (!connectorName) {
    console.warn(`Unable to extract managed API name from connector ID: ${connectorId}`);
    return [];
  }

  // Filter out triggers (properties/trigger eq null) to get only actions/tools
  const url = `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(opts.projectName)}/managedApis/${encodeURIComponent(connectorName)}/apiOperations?$top=200&api-version=${apiVersions.otto}&$filter=properties/trigger eq null`;

  try {
    const tools = await fetchWithPagination<McpToolOperation>(url, opts.accessToken);
    return tools;
  } catch (error) {
    console.warn(`Failed to fetch MCP tools for connector ${connectorId}:`, error);
    return [];
  }
}

/**
 * Converts an MCP server operation to a simplified server info object
 */
export function mcpServerToInfo(server: McpServerOperation): {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  iconUri?: string;
  brandColor?: string;
} {
  const props = server.properties;
  const api = props.api;

  const result: {
    id: string;
    name: string;
    displayName: string;
    description?: string;
    iconUri?: string;
    brandColor?: string;
  } = {
    id: server.id,
    name: server.name,
    displayName: api?.displayName ?? props.summary ?? server.name,
  };

  // Only add optional properties if they have values (exactOptionalPropertyTypes)
  const description = props.description ?? api?.description;
  if (description !== undefined) result.description = description;

  const iconUri = props.iconUri ?? api?.iconUri;
  if (iconUri !== undefined) result.iconUri = iconUri;

  const brandColor = typeof props.brandColor === 'string'
    ? props.brandColor
    : typeof props.brandColor === 'number'
      ? `#${props.brandColor.toString(16).padStart(6, '0')}`
      : api?.brandColor;
  if (brandColor !== undefined) result.brandColor = brandColor;

  return result;
}

/**
 * Converts an MCP tool operation to a simplified tool info object
 */
export function mcpToolToInfo(tool: McpToolOperation): {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  iconUri?: string;
  inputSchema?: Record<string, unknown>;
} {
  const props = tool.properties;

  const result: {
    id: string;
    name: string;
    displayName: string;
    description?: string;
    iconUri?: string;
    inputSchema?: Record<string, unknown>;
  } = {
    id: tool.id,
    name: tool.name,
    displayName: props.summary ?? tool.name,
  };

  // Only add optional properties if they have values (exactOptionalPropertyTypes)
  if (props.description !== undefined) result.description = props.description;

  const iconUri = props.iconUri ?? props.api?.iconUri;
  if (iconUri !== undefined) result.iconUri = iconUri;

  if (props.inputSchema !== undefined) result.inputSchema = props.inputSchema;

  return result;
}

/**
 * Connector ID used to represent the built-in MCP client provider in the
 * portal (parity with LogicAppsUX `MCP_CLIENT_CONNECTOR_ID`). The matching
 * runtime connection lives under `connections.json.agentMcpConnections`.
 */
export const MCP_CLIENT_CONNECTOR_ID = 'connectionProviders/mcpclient';

/**
 * Path segment that identifies a user-created custom connector. Otto exposes
 * first-party (Microsoft-managed) connectors under `/managedApis/` and
 * user-authored custom connectors under `/customApis/`. Unlike LogicAppsUX —
 * where the ARM `type` (`microsoft.web/locations/managedapis/apioperations`)
 * is the discriminator — Otto flattens every MCP operation to the single type
 * `automationProjects/managedApis/apiOperations`, so the connector-id path is
 * the reliable structural signal for the Microsoft-vs-custom split.
 */
const CUSTOM_CONNECTOR_PATH_SEGMENT = '/customapis/';

/**
 * Categorizes MCP servers into the shared (Microsoft-managed) bucket. Servers
 * missing required identifying fields are silently skipped so a malformed
 * control-plane row can't break the picker. Custom-connector servers
 * (`/customApis/`) are unsupported in Otto and are excluded entirely.
 *
 * Each operation row is treated as a distinct MCP server (a single connector
 * can expose multiple MCP servers/operations), so rows are not de-duplicated;
 * React keys stay unique via the operation `id`.
 *
 * @param servers - Array of MCP server operations from the control plane
 * @returns Object with the shared (Microsoft-managed) servers
 */
export function categorizeMcpServers(servers: McpServerOperation[]): {
  shared: McpServerOperation[];
} {
  const shared: McpServerOperation[] = [];

  for (const server of servers) {
    // Skip rows missing the fields the picker needs to render an item.
    const connectorId = server.properties?.api?.id;
    if (!connectorId || !server.id) continue;

    // Custom connectors (`/customApis/`) are unsupported in Otto; exclude them
    // so they never surface in the Microsoft tab. Everything else is a
    // first-party (Microsoft-managed) connector under `/managedApis/`.
    if (connectorId.toLowerCase().includes(CUSTOM_CONNECTOR_PATH_SEGMENT)) continue;

    shared.push(server);
  }

  return { shared };
}

// =============================================================================
// listMcpTools — dynamic tool discovery via the runtime
// =============================================================================

/**
 * MCP Tool returned from the listMcpTools backend API
 */
export interface McpToolFromBackend {
  name: string;
  description?: string;
  inputSchema?: Record<string, unknown>;
}

/**
 * Options for the listMcpTools API call
 *
 * Routes through Otto control plane:
 *   {logicEndpoint}/automationProjects/{projectName}/applications/{appName}/workflows/{workflow}/listMcpTools
 *
 * The control plane proxies the call to the runtime container.
 */
export interface ListMcpToolsOptions {
  projectName: string;
  appName: string;
  workflowName: string;
  accessToken: string;
}

/**
 * Connection info for listMcpTools API
 *
 * Two shapes are supported (matching backend `WebValidation.ListMcpToolsDefinition`):
 * - **Native**: `mcpServerUrl` is set. Used for direct, inline MCP server URLs.
 * - **Managed**: `connectionRuntimeUrl` + `authentication` (and usually `connectionProperties`)
 *   are set. Used for connection-reference-style MCP servers (most common).
 */
export interface McpConnectionInfo {
  /** Connection reference ID — for UI bookkeeping/cache keys only; not sent to backend */
  id?: string;

  /** Native mode: direct MCP server URL */
  mcpServerUrl?: string;

  /** Managed mode: runtime URL of the connection */
  connectionRuntimeUrl?: string;
  /** Managed mode: authentication block (see backend `WebValidation.ManagedMcpConnectionDetails`) */
  authentication?: Record<string, unknown>;
  /** Managed mode: connection-properties (often contains nested authentication) */
  connectionProperties?: Record<string, unknown>;
}

/**
 * Build the request body for listMcpTools.
 *
 * Mirrors the backend contract validated by `WebValidation.ListMcpToolsDefinition`
 * and `WebValidation.ManagedMcpConnectionDetails`:
 * - Native variant: `{ connection: { mcpServerUrl, authentication? } }`
 * - Managed variant: `{ managedConnection: { connectionRuntimeUrl, authentication, connectionProperties? }, mcpServerPath }`
 *   (managed mode REQUIRES connectionRuntimeUrl, authentication, AND mcpServerPath)
 */
function buildListMcpToolsBody(
  connection: McpConnectionInfo,
  mcpServerPath?: string
): Record<string, unknown> {
  // Native (inline) variant: requires mcpServerUrl. authentication is optional.
  if (connection.mcpServerUrl) {
    const nativeConnection: Record<string, unknown> = {
      mcpServerUrl: connection.mcpServerUrl,
    };

    if (connection.authentication) {
      nativeConnection.authentication = connection.authentication;
    }

    return { connection: nativeConnection };
  }

  // Managed variant: backend WebValidation.ManagedMcpConnectionDetails requires
  // connectionRuntimeUrl, authentication, and a non-empty mcpServerPath. Fail fast
  // here so we surface a clear error instead of a server-side 400.
  const missing: string[] = [];
  if (!connection.connectionRuntimeUrl) missing.push('connectionRuntimeUrl');
  if (!connection.authentication) missing.push('authentication');
  if (!mcpServerPath) missing.push('mcpServerPath');
  if (missing.length > 0) {
    throw new Error(
      `listMcpTools managed connection missing required field(s): ${missing.join(', ')}. ` +
        `Provide either an inline mcpServerUrl or a complete managed connection reference.`
    );
  }

  const managedConnection: Record<string, unknown> = {
    connectionRuntimeUrl: connection.connectionRuntimeUrl,
    authentication: connection.authentication,
  };

  if (connection.connectionProperties) {
    managedConnection.connectionProperties = connection.connectionProperties;
  }

  return { managedConnection, mcpServerPath };
}

/**
 * Lists available tools from an MCP server via the backend listMcpTools API.
 *
 * Routes through the Otto control plane (matching the rest of the portal's
 * project-scoped APIs) rather than calling the ACA FQDN directly. The control
 * plane forwards the request to the runtime container.
 *
 * Response shape (per backend `ManagedApiConnectionDynamicInvokeResponse`):
 *   { response: { statusCode: number, body: McpToolFromBackend[] } }
 *
 * In **local mode** the request goes to the local runtime; otherwise it goes
 * to the Otto control plane with an Otto bearer token.
 *
 * @param opts - Project / app / workflow identifiers + access token
 * @param connection - Connection info (native or managed variant)
 * @param mcpServerPath - Optional MCP server path
 * @returns Array of MCP tool definitions
 */
export async function listMcpTools(
  opts: ListMcpToolsOptions,
  connection: McpConnectionInfo,
  mcpServerPath?: string
): Promise<McpToolFromBackend[]> {
  const { projectName, appName, workflowName, accessToken } = opts;

  const body = buildListMcpToolsBody(connection, mcpServerPath);

  // Local mode: hit StandardRuntimeHost / func.exe directly under the
  // /runtime/webhooks/workflow/api/management path. No bearer needed — local
  // runtime trusts the caller identity headers `localFetch` injects.
  // (Mirrors the listApiKeys / listDraftApiKeys pattern in `agentUrlService.ts`.)
  if (isLocalMode()) {
    const localUrl = `${getLocalRuntimeUrl()}/runtime/webhooks/workflow/api/management/workflows/${workflowName}/listMcpTools?api-version=${LOCAL_RUNTIME_API_VERSION}`;
    const data = await localFetch<{ response?: { body?: unknown } }>(localUrl, {
      method: 'POST',
      body,
    });
    return Array.isArray(data.response?.body) ? data.response.body as McpToolFromBackend[] : [];
  }

  // Cloud / dev (mock control plane): go through the Otto control plane like
  // every other workflow-management call. The catch-all proxy forwards to ACA runtime.
  const url = `${getAppUrl({ projectName, appName })}/workflows/${workflowName}/listMcpTools?api-version=${apiVersions.otto}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`listMcpTools request failed: ${response.status} ${errorText}`);
  }

  const data = await response.json() as { response?: { body?: unknown } };
  return Array.isArray(data.response?.body) ? data.response.body as McpToolFromBackend[] : [];
}
